import { Link } from "react-router-dom";
import { ArrowRight, MapPin, CalendarClock, AlertCircle } from "lucide-react";
import { money } from "../../lib/api";
import { StatusBadge } from "../track/StatusBadge";
import { LongDistanceNote } from "../LongDistanceNote";

const fmt = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });

const QuoteCard = ({ c, onNavigate }) => (
  <div className="border border-amber-300 bg-amber-50/60 p-3.5" data-testid="chat-quote-card">
    <div className="text-[11px] font-mono uppercase tracking-wider text-slate-600">{c.origin} → {c.destination}</div>
    <div className="mt-1.5 flex items-end gap-2">
      <span className="font-display font-black text-3xl text-slate-900 leading-none" data-testid="chat-quote-estimate">{money(c.estimate)}</span>
      <span className="text-xs text-slate-600 pb-0.5">{c.minimum_applied ? "minimum charge" : `$${Number(c.rate_per_mile || 0).toFixed(2)}/mile`}</span>
    </div>
    <div className="mt-1 text-xs text-slate-700">{c.miles.toLocaleString()} mi · {c.transit_days_min}–{c.transit_days_max} days · {c.item_label}, {c.transport}{c.expedited ? ", expedited" : ""}{!c.operable ? ", inoperable" : ""}</div>
    <LongDistanceNote quote={c} compact testid="chat-quote-long-distance" />
    <p className="mt-2 text-[11px] text-slate-600">{c.disclaimer}</p>
    <Link to={c.book_path} onClick={onNavigate} className="btn-amber h-9 mt-2.5 w-full text-xs" data-testid="chat-quote-book">Book this shipment <ArrowRight className="w-3.5 h-3.5" /></Link>
  </div>
);

const TrackingCard = ({ c, onNavigate }) => (
  <div className="border border-slate-200 bg-white p-3.5" data-testid="chat-tracking-card">
    <div className="flex items-center justify-between gap-2">
      <span className="font-mono font-bold text-slate-900" data-testid="chat-tracking-number">{c.tracking_number}</span>
      <StatusBadge status={c.status} testid="chat-tracking-status" />
    </div>
    <div className="mt-2 text-xs text-slate-700">{c.item_summary}</div>
    <div className="text-xs text-slate-700">{c.origin} → {c.destination}</div>
    {c.current_location && <div className="mt-1.5 flex items-center gap-1 text-xs text-slate-800"><MapPin className="w-3 h-3 text-orange-600" />{c.current_location}</div>}
    {c.delivery_window?.start && <div className="mt-1 flex items-center gap-1 text-xs text-slate-800"><CalendarClock className="w-3 h-3 text-orange-600" />Window {fmt(c.delivery_window.start)}{c.delivery_window.end && c.delivery_window.end !== c.delivery_window.start ? ` – ${fmt(c.delivery_window.end)}` : ""}</div>}
    <Link to={c.track_path} onClick={onNavigate} className="btn-ghost h-9 mt-2.5 w-full text-xs" data-testid="chat-tracking-link">Open tracking page <ArrowRight className="w-3.5 h-3.5" /></Link>
  </div>
);

const Notice = ({ text, testid }) => (
  <div className="flex gap-2 border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700" data-testid={testid}><AlertCircle className="w-4 h-4 text-orange-600 shrink-0" />{text}</div>
);

export const ChatCards = ({ cards = [], onNavigate }) => (
  <div className="space-y-2">
    {cards.map((c, i) => {
      if (c.type === "quote") return <QuoteCard key={i} c={c} onNavigate={onNavigate} />;
      if (c.type === "tracking") return <TrackingCard key={i} c={c} onNavigate={onNavigate} />;
      if (c.type === "tracking_not_found") return <Notice key={i} testid="chat-tracking-not-found" text={`No shipment found for ${c.tracking_number}.`} />;
      if (c.type === "quote_error") return <Notice key={i} testid="chat-quote-error" text={c.message} />;
      return null;
    })}
  </div>
);
